import React, { useEffect, useRef } from 'react';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
  Badge,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Card,
  CardContent,
  Box
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import {
  recupereListeDemandesLegrand,
  recupereDemandeById,
  recupereInfosClientLegrand,
  majComLegrand,
  majComVisaLegrand,
  traitementAssuranceLegrand,
  supprimerComVisaEtPasserATraiterLegrand,
  getAffiliationAdresses,
  setAffiliationAdresse,
  marquerFactureRecuperee,
  archiverDemande,
  desarchiverDemande,
  recupererAlertesLegrand,
  creerAlerteLegrand,
  supprimerAlerteLegrand,
  AlerteDossier,
  creerDossierLegrand,
  erreurEmailLegrand,
  apiFormulairesLegrand
} from '../../services/legrandServices';
import { API_CONFIG, FILES_BASE_URL, getAuthHeader } from '../../api/config';
import { demandesLegrandService } from '../../api/legrand/demandesService';
import { Demandes } from '../../models/Demandes';
import DemandeActionsLegrand from './DemandeActionsLegrand';
import NouveauDossierDialog from '../NouveauDossierDialog';
import EtatVisaSelect from '../EtatVisaSelect';
import { ApiFormulairesProvider } from '../../formulaires/ApiFormulairesContext';
import { usePersistentState } from '../../hooks/usePersistentState';
import useIsMobile from '../../hooks/useIsMobile';

const getFormattedDate = () =>
  new Date()
    .toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    .replace(',', '');

const parseFrDateString = (s: string): Date | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const [, day, month, year] = m;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const formatDate = (input: string | Date): string => {
  if (input instanceof Date) {
    return input.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
  // Vérifier d'abord si c'est une date française (DD/MM/YYYY)
  const fr = parseFrDateString(input);
  if (fr) {
    return formatDate(fr);
  }
  // Si ce n'est pas une date française, essayer le parsing ISO
  const iso = new Date(input);
  if (!isNaN(iso.getTime())) {
    return formatDate(iso);
  }
  return input;
};

export const ListeDemandesLegrand: React.FC<{
  onSelectionDemande: (idVoyageur: number, chemin: string, pays: string, idDemande: number, email?: string) => void;
  onCollapsedChange?: (isCollapsed: boolean) => void;
}> = ({ onSelectionDemande, onCollapsedChange }) => {
  // \u00c9tats persistants
  const [visaFilter, setVisaFilter] = usePersistentState<'all' | 'visa' | 'evisa'>(
    'ld_legrand_visaFilter',
    'all'
  );
  const [etatFilter, setEtatFilter] = usePersistentState<string>(
    'ld_legrand_etatFilter',
    ''
  );
  const [searchTerm, setSearchTerm] = usePersistentState<string>(
    'ld_legrand_searchTerm',
    ''
  );
  const [selectedId, setSelectedId] = usePersistentState<number | null>(
    'ld_legrand_selectedId',
    null
  );

  // \u00c9tats volatiles
  const [demandes, setDemandes] = React.useState<Demandes[]>([]);
  const [filteredDemandes, setFilteredDemandes] = React.useState<Demandes[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);

  const [actionsOpen, setActionsOpen] = React.useState(false);

  const [allCommentaires, setAllCommentaires] = React.useState<string>('');
  const [commentaires, setCommentaires] = React.useState<string>('');
  const [commentairesVisa, setCommentairesVisa] = React.useState<string>('');
  const [allCommentairesVisa, setAllCommentairesVisa] = React.useState<string>('');

  const [openCommentaires, setOpenCommentaires] = React.useState(true);
  const [openCommentairesVisa, setOpenCommentairesVisa] = React.useState(true);

  // Création manuelle d'un dossier (superadmin, NouveauDossierDialog)
  const [nouveauDialogOpen, setNouveauDialogOpen] = React.useState(false);

  // Alertes planifiées du dossier sélectionné
  const [alertes, setAlertes] = React.useState<AlerteDossier[]>([]);
  const [alerteDialogOpen, setAlerteDialogOpen] = React.useState(false);
  const [alerteDate, setAlerteDate] = React.useState('');
  const [alerteHeure, setAlerteHeure] = React.useState('');
  const [alerteMessage, setAlerteMessage] = React.useState('');
  const [creationAlerteEnCours, setCreationAlerteEnCours] = React.useState(false);
  const [alerteErreur, setAlerteErreur] = React.useState<string | null>(null);

  const [clientInfoOpen, setClientInfoOpen] = React.useState(false);
  const [clientInfo, setClientInfo] = React.useState<any>(null);

  const [insuranceDialogOpen, setInsuranceDialogOpen] = React.useState(false);
  const [insuranceId, setInsuranceId] = React.useState<number | null>(null);

  const [optionsDialogOpen, setOptionsDialogOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<string[] | null>(null);

  const [affiliationDialogOpen, setAffiliationDialogOpen] = React.useState(false);
  const [selectedDemande, setSelectedDemande] = React.useState<Demandes | null>(null);
  const [affiliationAdresses, setAffiliationAdresses] = React.useState<any[]>([]);
  const [selectedAffiliationId, setSelectedAffiliationId] = React.useState<number | null>(null);
  const [addressDetailDialogOpen, setAddressDetailDialogOpen] = React.useState(false);
  const [selectedAddressDetail, setSelectedAddressDetail] = React.useState<any>(null);

  const [userRole, setUserRole] = React.useState<string>('admin');
  const [uploadFactureDialogOpen, setUploadFactureDialogOpen] = React.useState(false);
  const [factureFile, setFactureFile] = React.useState<File | null>(null);
  const [numeroFacture, setNumeroFacture] = React.useState<string>('');
  const [uploadingFacture, setUploadingFacture] = React.useState(false);

  const [filteredDemandeId, setFilteredDemandeId] = React.useState<number | null>(null);
  const [lastClickTime, setLastClickTime] = React.useState<number>(0);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showArchived, setShowArchived] = usePersistentState<boolean>(
    'ld_legrand_showArchived',
    false
  );
  const isMobile = useIsMobile();

  // Fonction pour r\u00e9cup\u00e9rer les demandes
  const fetchDemandes = async () => {
    try {
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur lors de la r\u00e9cup\u00e9ration des demandes Legrand:', error);
    }
  };

  // Fonction pour marquer les e-cards urgentes et filtrer
  const handleEcardUrgentes = async () => {
    try {
      const token = localStorage.getItem('token_legrand');
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Identifier les demandes éligibles (Inde eVisa / eCard et Togo eVisa)
      const demandesEligibles = demandes.filter((d) => {
        if (d.etat_ecard === 'traité' || d.etat_ecard === 'A traiter') return false;

        // Inde eVisa / eCard : calculé par le backend avec les critères du mail de la règle e-cards Inde du
        // menu Alertes (arrivée en Inde du formulaire sinon départ, dans 4 jours ou moins)
        if (d.ecard_urgente) return true;

        const isTogoEVisa = d.titre.toLowerCase().includes('togo') && d.titre.toLowerCase().includes('evisa');
        if (!isTogoEVisa) return false;

        // Pour Togo, utiliser dateSortie
        const dateToCheck = d.dateSortie || null;
        if (!dateToCheck) return false;

        // Parser la date
        let targetDate: Date | null = null;
        const frDate = parseFrDateString(dateToCheck);
        if (frDate) {
          targetDate = frDate;
        } else {
          const isoDate = new Date(dateToCheck);
          if (!isNaN(isoDate.getTime())) {
            targetDate = isoDate;
          }
        }

        if (!targetDate) return false;

        targetDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays < 5;
      });

      // Mettre à jour chaque demande éligible
      for (const demande of demandesEligibles) {
        await fetch(`${API_CONFIG.baseUrl}/api/legrand/demandes/${demande.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ etat_ecard: 'A traiter' })
        });
      }

      // Recharger les données et appliquer le filtre
      await fetchDemandes();
      setEtatFilter('e-card');
    } catch (error) {
      console.error('Erreur lors de la mise à jour des e-cards urgentes:', error);
    }
  };

  // R\u00e9cup\u00e9ration initiale des demandes
  useEffect(() => {
    fetchDemandes();

    // Récupérer le rôle de l'utilisateur depuis le localStorage
    const userData = localStorage.getItem('user_legrand');
    if (userData) {
      const user = JSON.parse(userData);
      setUserRole(user.role || 'admin');
    }
  }, [showArchived]);

  // Application des filtres et recherche
  useEffect(() => {
    let temp = [...demandes];

    // Si une demande est filtrée (double-clic), n'afficher que celle-ci
    if (filteredDemandeId !== null) {
      temp = temp.filter((d) => d.id === filteredDemandeId);
      setFilteredDemandes(temp);
      return;
    }

    // Debug: afficher les données brutes pour admin
    if (userRole === 'admin' && etatFilter) {
      console.log('=== Debug Filtres Admin ===');
      console.log('Filtre actuel:', etatFilter);
      console.log('Nombre de demandes avant filtre:', temp.length);
      temp.forEach(d => {
        console.log(`ID ${d.id}: etat_legrand="${d.etat_legrand || ''}" | etat_visa="${d.etat_visa}" | affiliation_nom="${d.affiliation_nom || ''}" | numfacture="${d.numfacture || ''}"`);
      });
    }

    // Filtre Visa / Evisa
    if (visaFilter === 'visa') {
      temp = temp.filter((d) => !d.titre.toLowerCase().includes('evisa'));
    } else if (visaFilter === 'evisa') {
      temp = temp.filter((d) => d.titre.toLowerCase().includes('evisa'));
    }

    // Filtre d'\u00e9tat
    switch (etatFilter) {
      case 'V\u00e9rification \u00e9ligibilit\u00e9':
        temp = temp.filter((d) => d.etat_visa === 'V\u00e9rification \u00e9ligibilit\u00e9');
        break;
      case '\u00c0 faire':
        temp = temp.filter(
          (d) =>
            d.etat_visa === '\u00e0 traiter' &&
            ['pay\u00e9', 'virement'].includes(d.etat_paiement)
        );
        break;
      case 'Attente visa':
        temp = temp.filter((d) => d.etat_visa === 'en cours');
        break;
      case 'Dossier en souffrance':
        temp = temp.filter((d) => d.etat_visa === 'Dossier en souffrance');
        break;
      case 'trait\u00e9':
        temp = temp.filter((d) => d.etat_visa === 'trait\u00e9');
        break;
      case 'Attente paiement':
        temp = temp.filter((d) => d.etat_paiement === 'attente paiement' || d.etat_paiement === 'Attente paiement' || d.etat_visa === 'Attente paiement');
        break;
      // Filtres spécifiques pour admin Legrand
      case 'en cours':
        // Tous les dossiers non traités
        temp = temp.filter((d) => d.etat_visa !== 'traité');
        break;
      case 'attente adresse':
        // Dossiers sans adresse d'affiliation (pas d'ID dans etat_legrand ou pas d'affiliation_nom)
        temp = temp.filter(
          (d) => {
            const hasNoAddress = (!d.etat_legrand || d.etat_legrand === '' || d.etat_legrand === null) && !d.affiliation_nom;
            return hasNoAddress;
          }
        );
        break;
      case 'attente facture':
        // Dossiers avec adresse d'affiliation mais sans facture
        temp = temp.filter(
          (d) => {
            // A une adresse d'affiliation (soit via affiliation_nom, soit etat_legrand a une valeur)
            const hasAddress = d.affiliation_nom || (d.etat_legrand && d.etat_legrand !== '' && d.etat_legrand !== null);
            // N'a pas de facture (etat_legrand ne contient pas _FACTUREE)
            const hasNoInvoice = !d.etat_legrand || !d.etat_legrand.includes('_FACTUREE');
            return hasAddress && hasNoInvoice;
          }
        );
        break;
      case 'facture à récupérer':
        // Dossiers avec facture uploadée mais pas encore récupérée
        temp = temp.filter(
          (d) => {
            const hasInvoice = d.etat_legrand && d.etat_legrand.includes('_FACTUREE');
            const notRetrieved = hasInvoice && !d.etat_legrand!.includes('_RECUPEREE');
            return hasInvoice && notRetrieved;
          }
        );
        break;
      case 'e-card':
        // Filtre pour les dossiers Inde eVisa et Togo eVisa avec etat_ecard = 'A traiter'
        temp = temp.filter((d) => {
          // Vérifier que c'est un Inde eVisa ou Togo eVisa
          const isIndeEVisa = d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('evisa');
          const isTogoEVisa = d.titre.toLowerCase().includes('togo') && d.titre.toLowerCase().includes('evisa');
          const isIndeECard = d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('ecard');

          // Filtrer sur etat_ecard = 'A traiter'
          return (isIndeEVisa || isTogoEVisa || isIndeECard) && d.etat_ecard === 'A traiter';
        });
        break;
      // Anciennes orthographes et celles de la liste des états (ETATS_VISA, changement d'état dans la table)
      case 'A vérifier':
        temp = temp.filter((d) => d.etat_visa === 'A vérifier' || d.etat_visa === 'à vérifier');
        break;
      case 'Attente réception dossier':
        temp = temp.filter((d) => d.etat_visa === 'Attente réception dossier' || d.etat_visa === 'En attente réception dossier');
        break;
      default:
        break;
    }

    // Debug: afficher le résultat après filtrage pour admin
    if (userRole === 'admin' && etatFilter) {
      console.log('Nombre après filtre:', temp.length);
      if (temp.length > 0) {
        console.log('Dossiers filtrés:');
        temp.forEach(d => {
          console.log(`  - ID ${d.id}: ${d.nom} ${d.prenom}`);
        });
      }
    }

    // Recherche textuelle
    if (searchTerm.trim() !== '') {
      const lower = searchTerm.toLowerCase();
      temp = temp.filter((d) => {
        const dCrea = formatDate(d.date_creation);
        const dDep = formatDate(d.datedepart);
        return (
          d.id?.toString().includes(lower) ||
          d.nom.toLowerCase().includes(lower) ||
          d.prenom.toLowerCase().includes(lower) ||
          d.titre.toLowerCase().includes(lower) ||
          d.etat_visa.toLowerCase().includes(lower) ||
          dCrea.includes(lower) ||
          dDep.includes(lower)
        );
      });
    }

    setFilteredDemandes(temp);
  }, [demandes, visaFilter, etatFilter, searchTerm, filteredDemandeId]);

  // Debug: surveiller les changements de allCommentaires
  useEffect(() => {
    console.log('allCommentaires a changé:', allCommentaires);
    console.log('allCommentaires longueur:', allCommentaires?.length);
  }, [allCommentaires]);

  // R\u00e9cup\u00e9ration de la ligne s\u00e9lectionn\u00e9e au chargement (une seule fois)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && selectedId && filteredDemandes.length > 0) {
      const dem = filteredDemandes.find((d) => d.id === selectedId);
      if (dem && dem.id !== null) {
        handleRowClick(dem.id, dem.id_voyageur, dem.chemin, dem.titre);
        setSelectedIds([dem.id]);
        hasInitialized.current = true;
      }
    }
  }, [selectedId, filteredDemandes]);

  // ---------- Alertes planifiées ----------
  // Le chargement est déclenché à chaque sélection de dossier : ce compteur écarte
  // la réponse d'un dossier précédent qui arriverait après celle du dossier courant.
  const alerteRequeteId = useRef(0);

  const loadAlertes = async (demandeId: number) => {
    const requete = ++alerteRequeteId.current;
    const data = await recupererAlertesLegrand(demandeId);
    if (requete === alerteRequeteId.current) {
      setAlertes(data);
    }
  };

  const formatAlerteDate = (dt: string) => {
    const d = new Date(dt.replace(' ', 'T'));
    if (isNaN(d.getTime())) return dt;
    return d
      .toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      .replace(',', '');
  };

  const fermerAlerteDialog = () => {
    setAlerteDialogOpen(false);
    setAlerteDate('');
    setAlerteHeure('');
    setAlerteMessage('');
    setAlerteErreur(null);
  };

  const handleCreerAlerte = async () => {
    if (selectedId === null || !alerteDate || !alerteHeure || !alerteMessage.trim()) return;
    const datetimeEnvoi = `${alerteDate} ${alerteHeure}:00`;
    setCreationAlerteEnCours(true);
    setAlerteErreur(null);
    try {
      await creerAlerteLegrand(selectedId, datetimeEnvoi, alerteMessage.trim());
      fermerAlerteDialog();
      await loadAlertes(selectedId);

      // Le backend a ajouté une ligne d'historique dans les commentaires du dossier.
      // Sans cette resynchronisation, `allCommentaires` reste sur la version chargée
      // au clic et la prochaine sauvegarde de commentaire écraserait cette ligne.
      // Son échec ne remet pas en cause la création : on ne le remonte pas à l'écran.
      try {
        const demandesAJour = await recupereListeDemandesLegrand(showArchived);
        setDemandes(demandesAJour);
        const dossier = demandesAJour.find((d) => d.id === selectedId);
        if (dossier) {
          setAllCommentaires(dossier.commentaires || '');
        }
      } catch (e) {
        console.error('Erreur resynchronisation des commentaires après création alerte', e);
      }
    } catch (e) {
      console.error('Erreur création alerte', e);
      setAlerteErreur("L'alerte n'a pas pu être créée. Réessayez, ou reconnectez-vous si le problème persiste.");
    } finally {
      setCreationAlerteEnCours(false);
    }
  };

  const handleSupprimerAlerte = async (alerteId: number) => {
    setAlerteErreur(null);
    try {
      await supprimerAlerteLegrand(alerteId);
      if (selectedId !== null) await loadAlertes(selectedId);
    } catch (e) {
      console.error('Erreur suppression alerte', e);
      setAlerteErreur("L'alerte n'a pas pu être supprimée. Réessayez, ou reconnectez-vous si le problème persiste.");
    }
  };

  // Sélection d'une ligne avec gestion du double-clic
  const handleRowClick = (
    id: number | null,
    idVoyageur: number,
    chemin: string,
    titre: string
  ) => {
    if (id === null) return;
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastClickTime;

    // Détection du double-clic (moins de 300ms entre les clics sur la même ligne)
    if (timeDiff < 300 && selectedId === id) {
      // Double-clic : basculer le filtrage
      if (filteredDemandeId === id) {
        // Si déjà filtré sur cette demande, retirer le filtre
        setFilteredDemandeId(null);
        onCollapsedChange?.(false);
      } else {
        // Sinon, filtrer sur cette demande
        setFilteredDemandeId(id);
        onCollapsedChange?.(true);
      }
    } else {
      // Simple clic : sélection normale + affichage des infos du dossier
      setSelectedId(id);
      setSelectedIds([id]);
      const dem = demandes.find((d) => d.id === id);
      onSelectionDemande(idVoyageur, chemin, titre, id, dem ? dem.email : undefined);
      onCollapsedChange?.(true);

      if (dem) {
        setSelectedEmail(dem.email);
        setAllCommentaires(dem.commentaires || '');
        setCommentaires('');
        setAllCommentairesVisa(dem.commentairesvisa || '');
        setCommentairesVisa('');
      }

      // Hors du `if` : les alertes se chargent même si le dossier n'est pas
      // (encore) présent dans `demandes`. On vide d'abord la liste, sinon celle du
      // dossier précédent reste affichée — et supprimable — pendant la requête.
      setAlertes([]);
      setAlerteErreur(null);
      loadAlertes(id);
    }

    setLastClickTime(currentTime);
  };


  // Fonctions pour archiver/désarchiver
  const handleArchiveDemande = async (id: number) => {
    try {
      await archiverDemande(id);
      await fetchDemandes();
    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
    }
  };

  const handleDesarchiveDemande = async (id: number) => {
    try {
      await desarchiverDemande(id);
      await fetchDemandes();
    } catch (error) {
      console.error('Erreur lors du désarchivage:', error);
    }
  };

  const handleClientInfo = async (email?: string) => {
    const targetEmail = email || selectedEmail;
    if (!targetEmail) return;
    try {
      const info = await recupereInfosClientLegrand(targetEmail);
      setClientInfo(info);
      setClientInfoOpen(true);
    } catch (error) {
      console.error('Erreur récupération info client:', error);
    }
  };

  const handleSaveCommentaires = async () => {
    const selectedDemande = demandes.find((d) => d.id !== null && selectedIds.includes(d.id));
    if (!selectedDemande) return;

    try {
      const timestampedComment = `${getFormattedDate()} - ${commentaires}\n`;
      const newCommentaires = allCommentaires + timestampedComment;
      await majComLegrand(selectedDemande.chemin, newCommentaires);
      setAllCommentaires(newCommentaires);
      setCommentaires('');

      // Mettre à jour localement la demande avec les commentaires NON formatés pour la BDD
      const updatedDemandes = demandes.map(d => {
        if (d.id === selectedDemande.id) {
          return { ...d, commentaires: newCommentaires };
        }
        return d;
      });
      setDemandes(updatedDemandes);

      // Rafra\u00eechir
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur sauvegarde commentaires:', error);
    }
  };

  const handleSaveCommentairesVisa = async () => {
    const selectedDemande = demandes.find((d) => d.id !== null && selectedIds.includes(d.id));
    if (!selectedDemande || selectedDemande.id === null || !commentairesVisa.trim()) return;

    try {
      // Relus juste avant : la route remplace tout le texte, et l'extension Chrome a pu ajouter une ligne depuis le
      // chargement de la liste
      const actuels = (await recupereDemandeById(selectedDemande.id)).commentairesvisa || '';
      const timestampedComment = `${getFormattedDate()} - ${commentairesVisa}\n`;
      const newCommentairesVisa = actuels + timestampedComment;
      await majComVisaLegrand(selectedDemande.chemin, newCommentairesVisa);
      setAllCommentairesVisa(newCommentairesVisa);
      setCommentairesVisa('');

      // Rafra\u00eechir
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur sauvegarde commentaires visa:', error);
    }
  };

  const handlePasserATraiter = async () => {
    const selectedDemande = demandes.find((d) => d.id !== null && selectedIds.includes(d.id));
    if (!selectedDemande) return;

    try {
      await supprimerComVisaEtPasserATraiterLegrand(selectedDemande.chemin);

      // Rafra\u00eechir
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur passage \u00e0 traiter:', error);
    }
  };

  // Fonctions pour gérer l'affiliation
  const fetchAffiliationAdresses = async () => {
    try {
      const adresses = await getAffiliationAdresses();
      setAffiliationAdresses(adresses);
    } catch (error) {
      console.error('Erreur lors de la récupération des adresses:', error);
    }
  };

  const handleOpenAffiliation = async (demande: Demandes) => {
    setSelectedDemande(demande);
    await fetchAffiliationAdresses();

    // Si la demande a déjà une affiliation, la sélectionner
    if (demande.etat_legrand) {
      setSelectedAffiliationId(parseInt(demande.etat_legrand));
    } else {
      setSelectedAffiliationId(null);
    }

    setAffiliationDialogOpen(true);
  };

  const handleSaveAffiliation = async () => {
    if (!selectedDemande || selectedDemande.id === null) return;

    try {
      await setAffiliationAdresse(selectedDemande.id, selectedAffiliationId);

      // Rafraîchir les demandes
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);

      setAffiliationDialogOpen(false);
      setSelectedDemande(null);
      setSelectedAffiliationId(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'affiliation:', error);
    }
  };

  const handleViewAddressDetail = (address: any) => {
    setSelectedAddressDetail(address);
    setAddressDetailDialogOpen(true);
  };

  // Fonctions pour gérer l'upload de facture (superadmin uniquement)
  const handleOpenUploadFacture = (demande: Demandes) => {
    if (userRole !== 'superadmin') return;
    setSelectedDemande(demande);
    setFactureFile(null);
    setNumeroFacture('');
    setUploadFactureDialogOpen(true);
  };

  const handleUploadFacture = async () => {
    if (!selectedDemande || selectedDemande.id === null || !factureFile || !numeroFacture.trim()) return;

    setUploadingFacture(true);
    try {
      const formData = new FormData();
      formData.append('facture', factureFile);
      formData.append('numeroFacture', numeroFacture.trim());

      const response = await fetch(
        `${API_CONFIG.baseUrl}/api/legrand/demandes/${selectedDemande.id}/facture`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeader('legrand'),
          },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Facture uploadée:', data);

        // Rafraîchir les demandes
        const result = await recupereListeDemandesLegrand(showArchived);
        setDemandes(result);

        // Réinitialiser et fermer
        setUploadFactureDialogOpen(false);
        setFactureFile(null);
        setNumeroFacture('');
      } else {
        const error = await response.json();
        console.error('Erreur lors de l\'upload de la facture:', error.message);
        alert(error.message || 'Erreur lors de l\'upload de la facture');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload de la facture:', error);
      alert('Erreur lors de l\'upload de la facture');
    } finally {
      setUploadingFacture(false);
    }
  };

  const handleInsurance = (id: number | null) => {
    if (id === null) return;
    setInsuranceId(id);
    setInsuranceDialogOpen(true);
  };

  const handleInsuranceConfirm = async () => {
    if (insuranceId === null) return;

    const demande = demandes.find(d => d.id === insuranceId);
    if (demande) {
      const newVal = demande.assurancevisa === 'oui' ? 'traité' : 'oui';
      try {
        await traitementAssuranceLegrand(insuranceId, newVal);
        setInsuranceDialogOpen(false);
        setInsuranceId(null);

        // Rafraîchir
        const result = await recupereListeDemandesLegrand(showArchived);
        setDemandes(result);
      } catch (error) {
        console.error('Erreur traitement assurance:', error);
      }
    }
  };

  const handleOpenOptions = (options: string) => {
    setSelectedOptions(options ? options.split(',').map((o) => o.trim()) : []);
    setOptionsDialogOpen(true);
  };

  // Fonction pour télécharger la facture
  const handleDownloadFacture = (demande: Demandes) => {
    if (!demande.numfacture || !demande.chemin) return;

    // Utiliser le numéro de facture dans le nom du fichier
    const urlFacture = `${FILES_BASE_URL}/factures/${demande.chemin}/facture-${demande.numfacture}.pdf`;
    window.open(urlFacture, '_blank');
  };

  // Fonction pour marquer la facture comme récupérée
  const handleMarquerFactureRecuperee = async (demande: Demandes) => {
    if (!demande.id || demande.id === null) return;

    try {
      await marquerFactureRecuperee(demande.id);
      // Rafraîchir les données
      const result = await recupereListeDemandesLegrand(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur lors du marquage de la facture:', error);
      alert('Erreur lors du marquage de la facture comme récupérée');
    }
  };

  // Changement d'état d'un dossier directement depuis la liste (superadmin), comme dans l'admin VisaMonde
  const handleChangeEtat = async (d: Demandes, nouvelEtat: string) => {
    if (userRole !== 'superadmin' || !d.id || nouvelEtat === d.etat_visa) return;
    const ancienEtat = d.etat_visa;
    // Mise à jour optimiste
    setDemandes((prev) =>
      prev.map((x) => (x.id === d.id ? { ...x, etat_visa: nouvelEtat } : x))
    );
    try {
      await demandesLegrandService.updateDemandeStatus(d.id, { etat_visa: nouvelEtat });
    } catch (error) {
      console.error("Erreur lors du changement d'état:", error);
      // Rollback en cas d'échec
      setDemandes((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, etat_visa: ancienEtat } : x))
      );
      window.alert("Erreur lors du changement d'état du dossier.");
    }
  };

  const getEtatColor = (etat: string) => {
    switch (etat) {
      case 'trait\u00e9':
        return '#4caf50';
      case 'en cours':
        return '#ff9800';
      case '\u00e0 traiter':
        return '#2196f3';
      case 'V\u00e9rification \u00e9ligibilit\u00e9':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  const getPaiementColor = (etat: string) => {
    switch (etat) {
      case 'pay\u00e9':
        return '#4caf50';
      case 'virement':
        return '#2196f3';
      case 'attente paiement':
        return '#ff9800';
      default:
        return '#757575';
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Rechercher"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        {/* Bouton Actions - Visible uniquement pour superadmin */}
        {userRole === 'superadmin' && (
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setActionsOpen(true)}
              disabled={selectedIds.length === 0}
            >
              Actions
            </Button>
          </Grid>
        )}
        {/* Nouveau dossier - Visible uniquement pour superadmin */}
        {userRole === 'superadmin' && (
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setNouveauDialogOpen(true)}
            >
              Nouveau dossier
            </Button>
          </Grid>
        )}
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              // Réinitialiser tous les filtres et la recherche
              setVisaFilter('all');
              setEtatFilter('');
              setSearchTerm('');
              // Recharger les données
              fetchDemandes();
            }}
          >
            Rafraîchir
          </Button>
        </Grid>
        {userRole !== 'admin' && (
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant={showArchived ? "contained" : "outlined"}
              color="primary"
              startIcon={<ArchiveIcon />}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? 'Masquer Archives' : 'Afficher Archives'}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Boutons de filtre rapide pour superadmin */}
      {userRole === 'superadmin' && (
        <>
          {/* Filtres Visa/eVisa */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant={visaFilter === 'all' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setVisaFilter('all')}
              >
                Tous
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={visaFilter === 'visa' ? 'contained' : 'outlined'}
                size="small"
                color="primary"
                onClick={() => setVisaFilter('visa')}
              >
                Visa
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={visaFilter === 'evisa' ? 'contained' : 'outlined'}
                size="small"
                color="secondary"
                onClick={() => setVisaFilter('evisa')}
              >
                eVisa
              </Button>
            </Grid>
          </Grid>

          {/* Filtres d'état */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant={etatFilter === 'en cours' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'en cours' ? '#2196f3' : undefined,
                  color: etatFilter === 'en cours' ? 'white' : '#2196f3',
                  '&:hover': { bgcolor: '#1976d2' }
                }}
                onClick={() => setEtatFilter('en cours')}
              >
                En cours
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'attente facture sa' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'attente facture sa' ? '#ff5722' : undefined,
                  color: etatFilter === 'attente facture sa' ? 'white' : '#ff5722',
                  '&:hover': { bgcolor: '#f4511e' }
                }}
                onClick={() => setEtatFilter('attente facture sa')}
              >
                Attente facture
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'À faire' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'À faire' ? '#ff9800' : undefined,
                  color: etatFilter === 'À faire' ? 'white' : '#ff9800',
                  '&:hover': { bgcolor: '#f57c00' }
                }}
                onClick={() => setEtatFilter('À faire')}
              >
                À faire
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'Attente visa' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'Attente visa' ? '#03a9f4' : undefined,
                  color: etatFilter === 'Attente visa' ? 'white' : '#03a9f4',
                  '&:hover': { bgcolor: '#039be5' }
                }}
                onClick={() => setEtatFilter('Attente visa')}
              >
                Attente visa
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'Dossier en souffrance' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'Dossier en souffrance' ? '#ff9800' : undefined,
                  color: etatFilter === 'Dossier en souffrance' ? 'white' : '#ff9800',
                  '&:hover': { bgcolor: '#f57c00' }
                }}
                onClick={() => setEtatFilter('Dossier en souffrance')}
              >
                Dossier en souffrance
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'traité' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'traité' ? '#4caf50' : undefined,
                  color: etatFilter === 'traité' ? 'white' : '#4caf50',
                  '&:hover': { bgcolor: '#45a049' }
                }}
                onClick={() => setEtatFilter('traité')}
              >
                Traités
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'Vérification éligibilité' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'Vérification éligibilité' ? '#9c27b0' : undefined,
                  color: etatFilter === 'Vérification éligibilité' ? 'white' : '#9c27b0',
                  '&:hover': { bgcolor: '#7b1fa2' }
                }}
                onClick={() => setEtatFilter('Vérification éligibilité')}
              >
                Vérification éligibilité
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'A vérifier' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'A vérifier' ? '#00bcd4' : undefined,
                  color: etatFilter === 'A vérifier' ? 'white' : '#00bcd4',
                  '&:hover': { bgcolor: '#0097a7' }
                }}
                onClick={() => setEtatFilter('A vérifier')}
              >
                A vérifier
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'Attente réception dossier' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'Attente réception dossier' ? '#795548' : undefined,
                  color: etatFilter === 'Attente réception dossier' ? 'white' : '#795548',
                  '&:hover': { bgcolor: '#5d4037' }
                }}
                onClick={() => setEtatFilter('Attente réception dossier')}
              >
                Attente réception
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={etatFilter === 'e-card' ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  bgcolor: etatFilter === 'e-card' ? '#ff6b35' : undefined,
                  color: etatFilter === 'e-card' ? 'white' : '#ff6b35',
                  '&:hover': { bgcolor: '#e55a2b' },
                  fontWeight: etatFilter === 'e-card' ? 'bold' : 'normal'
                }}
                onClick={handleEcardUrgentes}
              >
                E-card urgentes
              </Button>
            </Grid>
          </Grid>
        </>
      )}

      {/* Filtres spécifiques pour admin Legrand */}
      {userRole === 'admin' && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item>
            <Button
              variant={etatFilter === '' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setEtatFilter('')}
            >
              Tous
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'en cours' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'en cours' ? '#2196f3' : undefined,
                color: etatFilter === 'en cours' ? 'white' : '#2196f3',
                '&:hover': { bgcolor: '#1976d2' }
              }}
              onClick={() => setEtatFilter('en cours')}
            >
              En cours
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'attente adresse' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'attente adresse' ? '#ff9800' : undefined,
                color: etatFilter === 'attente adresse' ? 'white' : '#ff9800',
                '&:hover': { bgcolor: '#f57c00' }
              }}
              onClick={() => setEtatFilter('attente adresse')}
            >
              Attente adresse
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'attente facture' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'attente facture' ? '#ff5722' : undefined,
                color: etatFilter === 'attente facture' ? 'white' : '#ff5722',
                '&:hover': { bgcolor: '#f4511e' }
              }}
              onClick={() => setEtatFilter('attente facture')}
            >
              Attente facture
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'facture à récupérer' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'facture à récupérer' ? '#9c27b0' : undefined,
                color: etatFilter === 'facture à récupérer' ? 'white' : '#9c27b0',
                '&:hover': { bgcolor: '#7b1fa2' }
              }}
              onClick={() => setEtatFilter('facture à récupérer')}
            >
              Factures à récupérer
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'Dossier en souffrance' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'Dossier en souffrance' ? '#ff9800' : undefined,
                color: etatFilter === 'Dossier en souffrance' ? 'white' : '#ff9800',
                '&:hover': { bgcolor: '#f57c00' }
              }}
              onClick={() => setEtatFilter('Dossier en souffrance')}
            >
              Dossier en souffrance
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'traité' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'traité' ? '#4caf50' : undefined,
                color: etatFilter === 'traité' ? 'white' : '#4caf50',
                '&:hover': { bgcolor: '#45a049' }
              }}
              onClick={() => setEtatFilter('traité')}
            >
              Dossiers traités
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={etatFilter === 'e-card' ? 'contained' : 'outlined'}
              size="small"
              sx={{
                bgcolor: etatFilter === 'e-card' ? '#ff6b35' : undefined,
                color: etatFilter === 'e-card' ? 'white' : '#ff6b35',
                '&:hover': { bgcolor: '#e55a2b' },
                fontWeight: etatFilter === 'e-card' ? 'bold' : 'normal'
              }}
              onClick={handleEcardUrgentes}
            >
              E-card urgentes
            </Button>
          </Grid>
        </Grid>
      )}

      {!isMobile && (
      <TableContainer component={Paper} ref={tableContainerRef} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Date demande</TableCell>
              <TableCell>Pays</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Départ</TableCell>
              <TableCell>État</TableCell>
              <TableCell>E-card</TableCell>
              <TableCell>État Paiement</TableCell>
              <TableCell>N° Facture</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>{userRole !== 'admin' ? 'Client/Assurance/Archivage' : 'Client/Assurance'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDemandes.map((demande) => {
              const demandeId = demande.id || 0;
              return (
                <TableRow
                  key={demandeId}
                  hover
                  selected={demande.id !== null && demande.id === selectedId}
                  onClick={() => handleRowClick(demande.id, demande.id_voyageur, demande.chemin, demande.titre)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: demande.id === selectedId ? 'action.selected' : 'inherit'
                  }}
                  title="Double-cliquez pour filtrer/défiltrer cette ligne"
                >
                  <TableCell>{demande.id}</TableCell>
                  <TableCell>{formatDate(demande.date_creation)}</TableCell>
                  <TableCell>{demande.titre}</TableCell>
                  <TableCell>{demande.nom}</TableCell>
                  <TableCell>{demande.prenom}</TableCell>
                  <TableCell>{formatDate(demande.datedepart)}</TableCell>
                  <TableCell>
                    {userRole === 'superadmin' ? (
                      <EtatVisaSelect
                        value={demande.etat_visa}
                        titre={demande.titre}
                        getColor={getEtatColor}
                        onChange={(nouvelEtat) => handleChangeEtat(demande, nouvelEtat)}
                      />
                    ) : (
                      <span style={{ color: getEtatColor(demande.etat_visa), fontWeight: 'bold' }}>
                        {demande.etat_visa}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(((demande.titre.toLowerCase().includes('inde') || demande.titre.toLowerCase().includes('togo')) && demande.titre.toLowerCase().includes('evisa')) ||
                      (demande.titre.toLowerCase().includes('inde') && demande.titre.toLowerCase().includes('ecard')) ||
                      demande.titre.toLowerCase().includes('chine')) && demande.etat_ecard && (
                      <span style={{
                        color: demande.etat_ecard === 'traité' ? '#4caf50' : demande.etat_ecard === 'A traiter' ? '#00bcd4' : '#ff9800',
                        fontWeight: 'bold'
                      }}>
                        {demande.etat_ecard === 'traité' ? 'Traité' : demande.etat_ecard === 'A traiter' ? 'A traiter' : 'En attente'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {demande.etat_legrand && demande.etat_legrand.includes('_FACTUREE') ? (
                      // Facture déjà uploadée
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ReceiptIcon fontSize="small" color="success" />
                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Facturé</span>
                      </div>
                    ) : demande.etat_legrand && demande.affiliation_nom ? (
                      // Adresse d'affiliation associée
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ReceiptIcon fontSize="small" color="warning" />
                        <span style={{ color: '#ff9800', fontWeight: 'bold' }}>Attente facture</span>
                        <Tooltip title={`${demande.affiliation_nom}\n${demande.affiliation_adresse}\n${demande.affiliation_cp} ${demande.affiliation_ville}`}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAddressDetail({
                                nom: demande.affiliation_nom,
                                adresse: demande.affiliation_adresse,
                                cp: demande.affiliation_cp,
                                ville: demande.affiliation_ville
                              });
                            }}
                          >
                            <LocationOnIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {userRole === 'superadmin' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenUploadFacture(demande);
                            }}
                            title="Uploader une facture"
                            color="primary"
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    ) : (
                      // Aucune affiliation - tous les rôles peuvent associer une adresse
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAffiliation(demande);
                        }}
                        title="Associer une adresse de facturation"
                      >
                        <ReceiptIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {demande.numfacture ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{demande.numfacture}</span>
                        {demande.chemin && (
                          <>
                            {/* Bouton télécharger pour admin et superadmin */}
                            {(userRole === 'admin' || userRole === 'superadmin') && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFacture(demande);
                                }}
                                title="Télécharger la facture"
                                color="primary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            )}
                            {/* Bouton marquer récupérée - tous les rôles */}
                            {!demande.etat_legrand?.includes('_RECUPEREE') && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarquerFactureRecuperee(demande);
                                }}
                                title="Marquer comme récupérée"
                              >
                                <CheckCircleIcon fontSize="small" color="disabled" />
                              </IconButton>
                            )}
                          </>
                        )}
                        {demande.etat_legrand?.includes('_RECUPEREE') && (
                          <CheckCircleIcon fontSize="small" color="success" titleAccess="Facture récupérée" />
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {!demande.titre.toLowerCase().includes('evisa') && demande.options && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOptions(demande.options);
                        }}
                      >
                        <Badge
                          badgeContent={demande.options.split(',').filter(o => o.trim()).length}
                          color={demande.options.split(',').filter(o => o.trim()).length > 0 ? 'primary' : 'default'}
                        >
                          <InfoIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{demande.infosdemande}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientInfo(demande.email);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    {demande.assurancevisa !== 'non' && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsurance(demande.id);
                        }}
                      >
                        <AssignmentTurnedInIcon
                          style={{
                            color: demande.assurancevisa === 'traité' ? '#39FF14' : 'black'
                          }}
                        />
                      </IconButton>
                    )}
                    {userRole !== 'admin' && (
                      demande.archived ? (
                        <Tooltip title="Désarchiver">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDesarchiveDemande(demande.id!);
                            }}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Archiver">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveDemande(demande.id!);
                            }}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Vue mobile : liste de cartes (remplace le tableau sous 600px) */}
      {isMobile && (
        <Box>
          {filteredDemandes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucune demande
            </Typography>
          ) : (
            filteredDemandes.map((demande) => {
              const demandeId = demande.id || 0;
              const showEcard =
                (((demande.titre.toLowerCase().includes('inde') || demande.titre.toLowerCase().includes('togo')) && demande.titre.toLowerCase().includes('evisa')) ||
                  (demande.titre.toLowerCase().includes('inde') && demande.titre.toLowerCase().includes('ecard')) ||
                  demande.titre.toLowerCase().includes('chine')) && demande.etat_ecard;
              return (
                <Card
                  key={demandeId}
                  onClick={() => handleRowClick(demande.id, demande.id_voyageur, demande.chemin, demande.titre)}
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    borderLeft: `5px solid ${getEtatColor(demande.etat_visa)}`,
                    backgroundColor: demande.id === selectedId ? 'action.selected' : 'inherit',
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        #{demande.id} — {demande.nom} {demande.prenom}
                      </Typography>
                      {userRole === 'superadmin' ? (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <EtatVisaSelect
                            value={demande.etat_visa}
                            titre={demande.titre}
                            getColor={getEtatColor}
                            onChange={(nouvelEtat) => handleChangeEtat(demande, nouvelEtat)}
                            fontSize={13}
                          />
                        </Box>
                      ) : (
                        <span style={{ color: getEtatColor(demande.etat_visa), fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {demande.etat_visa}
                        </span>
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {demande.titre}
                    </Typography>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Demande : {formatDate(demande.date_creation)} · Départ : {formatDate(demande.datedepart)}
                    </Typography>

                    {showEcard && (
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: demande.etat_ecard === 'traité' ? '#4caf50' : demande.etat_ecard === 'A traiter' ? '#00bcd4' : '#ff9800' }}>
                        E-card : {demande.etat_ecard === 'traité' ? 'Traité' : demande.etat_ecard === 'A traiter' ? 'A traiter' : 'En attente'}
                      </Typography>
                    )}

                    {demande.infosdemande && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {demande.infosdemande}
                      </Typography>
                    )}

                    {/* Facturation (état paiement Legrand + N° facture) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }} onClick={(e) => e.stopPropagation()}>
                      {demande.etat_legrand && demande.etat_legrand.includes('_FACTUREE') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <ReceiptIcon fontSize="small" color="success" />
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Facturé</span>
                        </div>
                      ) : demande.etat_legrand && demande.affiliation_nom ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <ReceiptIcon fontSize="small" color="warning" />
                          <span style={{ color: '#ff9800', fontWeight: 'bold' }}>Attente facture</span>
                          <Tooltip title={`${demande.affiliation_nom}\n${demande.affiliation_adresse}\n${demande.affiliation_cp} ${demande.affiliation_ville}`}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewAddressDetail({ nom: demande.affiliation_nom, adresse: demande.affiliation_adresse, cp: demande.affiliation_cp, ville: demande.affiliation_ville }); }}>
                              <LocationOnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {userRole === 'superadmin' && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenUploadFacture(demande); }} title="Uploader une facture" color="primary">
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                      ) : (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAffiliation(demande); }} title="Associer une adresse de facturation">
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}

                      {demande.numfacture && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span>Facture : {demande.numfacture}</span>
                          {demande.chemin && (
                            <>
                              {(userRole === 'admin' || userRole === 'superadmin') && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadFacture(demande); }} title="Télécharger la facture" color="primary">
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                              {!demande.etat_legrand?.includes('_RECUPEREE') && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarquerFactureRecuperee(demande); }} title="Marquer comme récupérée">
                                  <CheckCircleIcon fontSize="small" color="disabled" />
                                </IconButton>
                              )}
                            </>
                          )}
                          {demande.etat_legrand?.includes('_RECUPEREE') && (
                            <CheckCircleIcon fontSize="small" color="success" titleAccess="Facture récupérée" />
                          )}
                        </div>
                      )}
                    </Box>

                    {/* Actions : options, client, assurance, archivage */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 1 }} onClick={(e) => e.stopPropagation()}>
                      {!demande.titre.toLowerCase().includes('evisa') && demande.options && (
                        <Tooltip title="Options">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenOptions(demande.options); }}>
                            <Badge
                              badgeContent={demande.options.split(',').filter(o => o.trim()).length}
                              color={demande.options.split(',').filter(o => o.trim()).length > 0 ? 'primary' : 'default'}
                            >
                              <InfoIcon fontSize="small" />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Infos client">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClientInfo(demande.email); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {demande.assurancevisa !== 'non' && (
                        <Tooltip title="Assurance">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleInsurance(demande.id); }}>
                            <AssignmentTurnedInIcon style={{ color: demande.assurancevisa === 'traité' ? '#39FF14' : 'black' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {userRole !== 'admin' && (
                        demande.archived ? (
                          <Tooltip title="Désarchiver">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDesarchiveDemande(demande.id!); }}>
                              <UnarchiveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archiver">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleArchiveDemande(demande.id!); }}>
                              <ArchiveIcon />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      )}

      {/* Section Commentaires - Visible uniquement pour superadmin */}
      {console.log('Condition affichage commentaires:', selectedIds.length === 1, 'selectedIds:', selectedIds, 'userRole:', userRole)}
      {selectedIds.length === 1 && userRole === 'superadmin' && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">Commentaires</Typography>
                <IconButton onClick={() => setOpenCommentaires(!openCommentaires)}>
                  {openCommentaires ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
              <Collapse in={openCommentaires}>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  variant="outlined"
                  value={allCommentaires}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ mb: 1, backgroundColor: '#f5f5f5' }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Ajouter un commentaire..."
                  value={commentaires}
                  onChange={(e) => setCommentaires(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSaveCommentaires}
                  disabled={!commentaires.trim()}
                >
                  Enregistrer
                </Button>
              </Collapse>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">Commentaires Visa</Typography>
                <IconButton onClick={() => setOpenCommentairesVisa(!openCommentairesVisa)}>
                  {openCommentairesVisa ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
              <Collapse in={openCommentairesVisa}>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  variant="outlined"
                  value={allCommentairesVisa}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ mb: 1, backgroundColor: '#f5f5f5' }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Nouveau commentaire visa..."
                  value={commentairesVisa}
                  onChange={(e) => setCommentairesVisa(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSaveCommentairesVisa}
                  disabled={!commentairesVisa.trim()}
                >
                  Enregistrer
                </Button>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Alertes planifiées du dossier — accessible à tous les rôles Legrand */}
      {selectedIds.length === 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Alertes planifiées
          </Typography>
          <Button
            variant="outlined"
            startIcon={<NotificationsActiveIcon />}
            onClick={() => {
              setAlerteErreur(null);
              setAlerteDialogOpen(true);
            }}
          >
            Créer une alerte
          </Button>
          {alerteErreur && !alerteDialogOpen && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {alerteErreur}
            </Typography>
          )}
          {alertes.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {alertes.map((al) => {
                // Une alerte typée est un marqueur posé par un cron (ex. relance e-card) :
                // son message porte l'information, et l'API refuse sa suppression.
                const auto = !!al.type;
                return (
                  <div
                    key={al.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: al.statut === 'envoye' ? '#888' : '#333'
                    }}
                  >
                    <NotificationsActiveIcon
                      fontSize="small"
                      color={auto ? 'success' : al.statut === 'envoye' ? 'disabled' : 'warning'}
                    />
                    <span>
                      {formatAlerteDate(al.datetime_envoi)}
                      {auto
                        ? ` — ${al.message}`
                        : al.statut === 'envoye' ? ' — envoyée' : ''}
                    </span>
                    {!auto && (
                      <IconButton size="small" onClick={() => handleSupprimerAlerte(al.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Paper>
      )}

      {/* Nouveau dossier (superadmin) : même dialog que l'admin, création et formulaires par l'API Legrand */}
      {userRole === 'superadmin' && (
        <ApiFormulairesProvider value={apiFormulairesLegrand}>
          <NouveauDossierDialog
            open={nouveauDialogOpen}
            onClose={() => setNouveauDialogOpen(false)}
            creerDossier={creerDossierLegrand}
            onCree={fetchDemandes}
            erreurEmail={erreurEmailLegrand}
            aideEmail="Adresse @legrand.com ou @legrand.fr du salarié"
          />
        </ApiFormulairesProvider>
      )}

      {/* Dialog Actions */}
      {actionsOpen && (
        <DemandeActionsLegrand
          open={actionsOpen}
          onClose={() => setActionsOpen(false)}
          selectedIds={selectedIds}
          email={selectedEmail || ''}
          onRefresh={async () => {
            const result = await recupereListeDemandesLegrand(showArchived);
            setDemandes(result);
          }}
        />
      )}

      {/* Dialog Info Client */}
      <Dialog open={clientInfoOpen} onClose={() => setClientInfoOpen(false)}>
        <DialogTitle>Informations Client</DialogTitle>
        <DialogContent>
          {clientInfo && (
            <div>
              <Typography>Nom: {clientInfo.nom}</Typography>
              <Typography>Email: {clientInfo.email}</Typography>
              <Typography>Téléphone: {clientInfo.telephone}</Typography>
              <Typography>Chemin: {clientInfo.chemin}</Typography>
              <Typography>Date inscription: {formatDate(clientInfo.date_inscription)}</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientInfoOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Assurance */}
      <Dialog open={insuranceDialogOpen} onClose={() => setInsuranceDialogOpen(false)}>
        <DialogTitle>Traitement de l'assurance</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir changer l'état de l'assurance (oui ⇄ traité) ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsuranceDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleInsuranceConfirm} color="secondary">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Options */}
      <Dialog open={optionsDialogOpen} onClose={() => setOptionsDialogOpen(false)}>
        <DialogTitle>Options s\u00e9lectionn\u00e9es</DialogTitle>
        <DialogContent>
          {selectedOptions && selectedOptions.length > 0 ? (
            <ul>
              {selectedOptions.map((option, index) => (
                <li key={index}>{option}</li>
              ))}
            </ul>
          ) : (
            <Typography>Aucune option</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Affiliation */}
      <Dialog open={affiliationDialogOpen} onClose={() => setAffiliationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Associer une adresse de facturation</DialogTitle>
        <DialogContent>
          {selectedDemande && (
            <div>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Demande #{selectedDemande.id} - {selectedDemande.nom} {selectedDemande.prenom}
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <Select
                  value={selectedAffiliationId || ''}
                  onChange={(e) => setSelectedAffiliationId(e.target.value ? Number(e.target.value) : null)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Aucune affiliation</em>
                  </MenuItem>
                  {affiliationAdresses.map((adresse) => (
                    <MenuItem key={adresse.id} value={adresse.id}>
                      {adresse.nom} - {adresse.ville} ({adresse.cp})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedAffiliationId && affiliationAdresses.find(a => a.id === selectedAffiliationId) && (
                <Paper sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom>Adresse sélectionnée:</Typography>
                  {(() => {
                    const addr = affiliationAdresses.find(a => a.id === selectedAffiliationId);
                    return addr ? (
                      <>
                        <Typography>{addr.nom}</Typography>
                        <Typography>{addr.adresse}</Typography>
                        <Typography>{addr.cp} {addr.ville}</Typography>
                      </>
                    ) : null;
                  })()}
                </Paper>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAffiliationDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleSaveAffiliation} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détail Adresse */}
      <Dialog open={addressDetailDialogOpen} onClose={() => setAddressDetailDialogOpen(false)}>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <LocationOnIcon color="primary" />
            Adresse de facturation
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedAddressDetail && (
            <div>
              <Typography variant="h6" gutterBottom>{selectedAddressDetail.nom}</Typography>
              <Typography>{selectedAddressDetail.adresse}</Typography>
              <Typography>{selectedAddressDetail.cp} {selectedAddressDetail.ville}</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDetailDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Upload Facture (superadmin uniquement) */}
      {userRole === 'superadmin' && (
        <Dialog open={uploadFactureDialogOpen} onClose={() => {
          setUploadFactureDialogOpen(false);
          setFactureFile(null);
          setNumeroFacture('');
        }} maxWidth="sm" fullWidth>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ReceiptIcon color="primary" />
              Uploader une facture
            </div>
          </DialogTitle>
          <DialogContent>
            {selectedDemande && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Demande #{selectedDemande.id} - {selectedDemande.nom} {selectedDemande.prenom}
                </Typography>

                {selectedDemande.affiliation_nom && (
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom>Adresse de facturation:</Typography>
                    <Typography>{selectedDemande.affiliation_nom}</Typography>
                    <Typography>{selectedDemande.affiliation_adresse}</Typography>
                    <Typography>{selectedDemande.affiliation_cp} {selectedDemande.affiliation_ville}</Typography>
                  </Paper>
                )}

                <TextField
                  fullWidth
                  label="Numéro de facture"
                  value={numeroFacture}
                  onChange={(e) => setNumeroFacture(e.target.value)}
                  variant="outlined"
                  required
                  sx={{ mb: 2 }}
                  helperText="Ex: 2024-001, INV-12345, etc."
                />

                <Button
                  variant="contained"
                  component="label"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {factureFile ? factureFile.name : 'Sélectionner un fichier PDF'}
                  <input
                    type="file"
                    hidden
                    accept="application/pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setFactureFile(e.target.files[0]);
                      }
                    }}
                  />
                </Button>

                {factureFile && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Fichier sélectionné: {factureFile.name} ({(factureFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUploadFactureDialogOpen(false);
              setFactureFile(null);
              setNumeroFacture('');
            }} disabled={uploadingFacture}>
              Annuler
            </Button>
            <Button
              onClick={handleUploadFacture}
              variant="contained"
              color="primary"
              disabled={!factureFile || !numeroFacture.trim() || uploadingFacture}
            >
              {uploadingFacture ? 'Upload en cours...' : 'Uploader'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog Créer une alerte */}
      <Dialog open={alerteDialogOpen} onClose={fermerAlerteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Créer une alerte</DialogTitle>
        <DialogContent>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={alerteDate}
            onChange={(e) => setAlerteDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: 10 }}
          />
          <TextField
            label="Heure"
            type="time"
            fullWidth
            value={alerteHeure}
            onChange={(e) => setAlerteHeure(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: 16 }}
          />
          <TextField
            label="Message"
            multiline
            rows={3}
            fullWidth
            value={alerteMessage}
            onChange={(e) => setAlerteMessage(e.target.value)}
            style={{ marginTop: 16 }}
          />
          <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Le message sera envoyé par mail à contact@visamonde.com à la date et l'heure
            choisies.
          </p>
          {alerteErreur && (
            <p style={{ fontSize: 12, color: '#d32f2f', marginTop: 8 }}>{alerteErreur}</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fermerAlerteDialog} disabled={creationAlerteEnCours}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleCreerAlerte}
            disabled={
              !alerteDate || !alerteHeure || !alerteMessage.trim() || creationAlerteEnCours
            }
          >
            {creationAlerteEnCours ? 'Création...' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
